import React, { useMemo } from "react";
import { Box, Chip, Typography, alpha, Stack, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

// ── Colores ─────────────────────────────────────────────────────────
const OWNERSHIP_COLORS = {
  mine: { bg: "#EFF6FF", border: "#3B82F6", dot: "#3B82F6" },
  delegated: { bg: "#FFFBEB", border: "#F59E0B", dot: "#F59E0B" },
};

const STATUS_CONFIG = {
  pending: { label: "Pending", bg: "#FEF3C7", color: "#92400E" },
  in_progress: { label: "In Progress", bg: "#DBEAFE", color: "#1E40AF" },
  completed: { label: "Completed", bg: "#D1FAE5", color: "#065F46" },
  cancelled: { label: "Cancelled", bg: "#F3F4F6", color: "#6B7280" },
};

const PRIORITY_CONFIG = {
  high: { label: "High", bg: "#FEE2E2", color: "#991B1B" },
  medium: { label: "Medium", bg: "#FEF3C7", color: "#92400E" },
  low: { label: "Low", bg: "#E0E7FF", color: "#3730A3" },
};

/**
 * Tabla de tareas con colores de ownership (mine vs delegated).
 */
export default function TasksTable({ tasks, currentEmployeeId, onRowClick, loading }) {
  const rows = useMemo(
    () =>
      tasks.map((t) => ({
        ...t,
        isMine: String(t.assigneeId) === String(currentEmployeeId),
        isCreator: String(t.creatorId) === String(currentEmployeeId),
      })),
    [tasks, currentEmployeeId]
  );

  const columns = useMemo(
    () => [
      {
        field: "ownership",
        headerName: "",
        width: 50,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const ownerCfg = row.isMine ? OWNERSHIP_COLORS.mine : OWNERSHIP_COLORS.delegated;
          const icon = row.isMine ? (
            <PersonIcon sx={{ fontSize: 18, color: ownerCfg.dot }} />
          ) : (
            <PersonOutlineIcon sx={{ fontSize: 18, color: ownerCfg.dot }} />
          );
          return (
            <Tooltip title={row.isMine ? "Assigned to me" : "Delegated to others"}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  bgcolor: ownerCfg.bg,
                  border: `1.5px solid ${ownerCfg.border}`,
                }}
              >
                {icon}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "title",
        headerName: "Task",
        flex: 1,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={{ py: 0.5 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{
                textDecoration: row.status === "completed" ? "line-through" : "none",
                color: row.status === "cancelled" ? "text.disabled" : "text.primary",
              }}
            >
              {row.title}
            </Typography>
            {row.patientName && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.patientName}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: ({ value }) => {
          const cfg = STATUS_CONFIG[value] || STATUS_CONFIG.pending;
          return (
            <Chip
              label={cfg.label}
              size="small"
              sx={{
                height: 24,
                fontSize: "0.72rem",
                fontWeight: 600,
                bgcolor: cfg.bg,
                color: cfg.color,
              }}
            />
          );
        },
      },
      {
        field: "priority",
        headerName: "Priority",
        width: 110,
        renderCell: ({ value }) => {
          const cfg = PRIORITY_CONFIG[value] || PRIORITY_CONFIG.medium;
          return (
            <Chip
              label={cfg.label}
              size="small"
              sx={{
                height: 24,
                fontSize: "0.72rem",
                fontWeight: 600,
                bgcolor: cfg.bg,
                color: cfg.color,
              }}
            />
          );
        },
      },
      {
        field: "dueDate",
        headerName: "Due Date",
        width: 120,
        renderCell: ({ value }) => {
          if (!value) return <Typography variant="caption" color="text.disabled">No date</Typography>;
          const isOverdue = new Date(value) < new Date() && value;
          return (
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ color: isOverdue ? "#DC2626" : "text.primary" }}
            >
              {value}
            </Typography>
          );
        },
      },
      {
        field: "assigneeName",
        headerName: "Assignee",
        width: 180,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: row.isMine ? OWNERSHIP_COLORS.mine.dot : OWNERSHIP_COLORS.delegated.dot,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" noWrap>
              {row.assigneeName || "Unassigned"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "createdBy",
        headerName: "Created By",
        width: 160,
        renderCell: ({ value }) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {value || "—"}
          </Typography>
        ),
      },
      {
        field: "category",
        headerName: "Category",
        width: 140,
        renderCell: ({ value }) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {value || "—"}
          </Typography>
        ),
      },
    ],
    []
  );

  return (
    <Box
      sx={{
        height: 480,
        "& .MuiDataGrid-root": {
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
        },
        "& .MuiDataGrid-columnHeaders": {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: 700,
          fontSize: "0.8rem",
          textTransform: "uppercase",
          color: "text.secondary",
        },
        "& .MuiDataGrid-row": {
          cursor: "pointer",
          transition: "background-color 0.15s",
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          },
        },
        "& .row-mine": {
          borderLeft: `3px solid ${OWNERSHIP_COLORS.mine.border}`,
        },
        "& .row-delegated": {
          borderLeft: `3px solid ${OWNERSHIP_COLORS.delegated.border}`,
        },
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        disableSelectionOnClick
        getRowClassName={({ row }) => (row.isMine ? "row-mine" : "row-delegated")}
        onRowClick={({ row }) => onRowClick?.(row)}
        initialState={{
          sorting: {
            sortModel: [{ field: "dueDate", sort: "asc" }],
          },
        }}
        sx={{
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
        }}
      />
    </Box>
  );
}
